import { reportAuthFlowBlocked } from '@peanuds/shared/observability/runtime-observability';

import {
  AUTH0_SESSION_TIMEOUT_ERROR_MESSAGE,
  AUTH0_STATE_POLL_INTERVAL_MS,
  ID_TOKEN_RETRY_INTERVAL_MS,
  sleep,
  withTimeout,
} from '../session/sync-utils';
import { getTokenOrganizationId } from '../session/token-claims';
import type { Auth0ClientLike } from '../session/types';

export type Auth0SessionTokens = {
  accessToken: string;
  idToken: string;
};

type DetailedTokenResponse = {
  access_token?: string;
  id_token?: string;
};

type DetailedAccessTokenRequestOptions = {
  detailedResponse: true;
  cacheMode?: 'off';
  ignoreCache?: boolean;
  authorizationParams?: {
    organization?: string;
  };
};

const reportAuth0SessionRefreshError = (operation: string, error: unknown) => {
  console.warn(`[Auth0] ${operation} failed during session refresh.`, error);
  reportAuthFlowBlocked({
    operation,
    failureKind: 'session-refresh-error',
    tags: {
      authSessionStage: 'session-refresh',
    },
  });
};

const isDetailedTokenResponse = (
  tokenResponse: string | DetailedTokenResponse,
): tokenResponse is DetailedTokenResponse => {
  return typeof tokenResponse !== 'string';
};

const getIdTokenWithRetry = async (
  auth0Client: Auth0ClientLike,
  timeoutMs?: number,
): Promise<string | null> => {
  const getIdTokenClaims = auth0Client.getIdTokenClaims;
  const idTokenClaimsRef = auth0Client.idTokenClaims;

  if (!getIdTokenClaims && !idTokenClaimsRef) {
    return null;
  }

  const startedAt = Date.now();

  while (true) {
    const claimsFromMethod = getIdTokenClaims
      ? await getIdTokenClaims().catch((error: unknown) => {
          reportAuth0SessionRefreshError('getIdTokenClaims', error);
          throw error;
        })
      : undefined;
    const rawToken = claimsFromMethod?.__raw ?? idTokenClaimsRef?.value?.__raw ?? null;

    if (rawToken) {
      return rawToken;
    }

    if (!timeoutMs) {
      return null;
    }

    const elapsedMs = Date.now() - startedAt;
    const remainingMs = timeoutMs - elapsedMs;

    if (remainingMs <= 0) {
      return null;
    }

    await sleep(Math.min(ID_TOKEN_RETRY_INTERVAL_MS, remainingMs));
  }
};

export const waitForAuth0ToSettle = async (
  auth0Client: Auth0ClientLike | null,
  timeoutMs?: number,
) => {
  if (!auth0Client?.isLoading) {
    return;
  }

  const startedAt = Date.now();

  while (auth0Client.isLoading.value) {
    if (!timeoutMs) {
      await sleep(AUTH0_STATE_POLL_INTERVAL_MS);
      continue;
    }

    const elapsedMs = Date.now() - startedAt;
    const remainingMs = timeoutMs - elapsedMs;

    if (remainingMs <= 0) {
      throw new Error(AUTH0_SESSION_TIMEOUT_ERROR_MESSAGE);
    }

    await sleep(Math.min(AUTH0_STATE_POLL_INTERVAL_MS, remainingMs));
  }
};

const assertOrganizationScopedToken = (token: string, organization?: string | null) => {
  if (organization && getTokenOrganizationId(token) !== organization) {
    throw new Error('Missing organization-scoped access token');
  }
};

export const getSessionFromAuth0 = async ({
  auth0Client,
  freshAuth0Token,
  organization,
  timeoutMs,
}: {
  auth0Client: Auth0ClientLike;
  freshAuth0Token?: boolean;
  organization?: string | null;
  timeoutMs?: number;
}): Promise<Auth0SessionTokens> => {
  const shouldBypassCache = freshAuth0Token || !!organization;

  const tokenResponse = await withTimeout(
    (
      auth0Client.getAccessTokenSilently?.({
        detailedResponse: true,
        ...(shouldBypassCache ? { cacheMode: 'off', ignoreCache: true } : {}),
        ...(organization ? { authorizationParams: { organization } } : {}),
      } satisfies DetailedAccessTokenRequestOptions) ??
      Promise.reject(new Error('Missing Auth0 client access token loader'))
    ).catch((error: unknown) => {
      reportAuth0SessionRefreshError('getAccessTokenSilently', error);
      throw error;
    }),
    timeoutMs,
  );

  const accessToken = isDetailedTokenResponse(tokenResponse)
    ? (tokenResponse.access_token ?? null)
    : tokenResponse;

  if (!accessToken) {
    throw new Error('Missing access token');
  }

  assertOrganizationScopedToken(accessToken, organization);

  const idTokenFromResponse = isDetailedTokenResponse(tokenResponse)
    ? (tokenResponse.id_token ?? null)
    : null;
  const idToken = await withTimeout(
    Promise.resolve(idTokenFromResponse).then(async (resolvedIdToken) => {
      if (resolvedIdToken) {
        return resolvedIdToken;
      }

      return getIdTokenWithRetry(auth0Client, timeoutMs);
    }),
    timeoutMs,
  );

  if (!idToken) {
    throw new Error('Missing ID token');
  }

  assertOrganizationScopedToken(idToken, organization);

  return {
    accessToken,
    idToken,
  };
};
